import { ref } from "vue";
import {
  injectAppConfirm,
  injectSnackbarError
} from "@/composables/injections";
import { useTranslation } from "@/plugins/i18next/i18next";
import { apolloClient } from "@/plugins/apollo";
import { useQuery } from "@vue/apollo-composable";

import gql from "fraql";
import { useCurrency } from "@composables/masterdata/config";
import { getFederationConfiguration } from "@composables/masterdata/config";

import AppShowEntryResolution from "@AppShows/AppShowEntryResolution.vue";
import AppShowPeopleList from "@AppShows/person/AppShowPeopleList.vue";
import AppShowHorsesList from "@AppShows/horse/AppShowHorsesList.vue";
import AppHorseSlot from "@elements/AppHorseSlot.vue";
import AppShowCompetitionCodeAndName from "@AppShows/event/AppShowCompetitionCodeAndName.vue";
import { orderBy, compact, uniqBy } from "lodash-es";
import { showLoadingButton } from "@/utils/showLoadingButton";

export function useCashierHelpers(props) {
  const { showDisplayName } = getFederationConfiguration();
  const { masterCurrency } = useCurrency();

  const { appConfirm } = injectAppConfirm();
  const { snackbarServerError } = injectSnackbarError();
  const { t } = useTranslation();

  const balances = ref([]);
  const returns = ref({});

  const event = ref({});

  async function unregister(item, evt) {
    let content = `${t("events:unregister.modal.question")}`;
    content += `<br><b>${t(
      "events:unregister.modal.athlete.label"
    )} ${showDisplayName(item.athletes[0])}</b>`;

    if (item.horses[0]) {
      content += `<br><b>${t("events:unregister.modal.horse.label")} ${
        item.horses[0].name
      }</b> `;
    }

    const response = await appConfirm(
      t("events:unregister.modal.heading"),
      content
    );

    if (!response) return;

    const removeLoading = showLoadingButton(evt.target);
    apolloClient
      .mutate({
        mutation: gql`
          mutation registrationUnregister($id: ID) {
            registrationUnregister(input: { id: $id }) {
              errors
            }
          }
        `,
        variables: {
          id: item.id
        }
      })
      .then(({ data }) => {
        removeLoading();

        if (data.errors && data.errors.length > 0) {
          snackbarServerError(data.errors, {
            level: "error"
          });
        } else {
          cashierRefetch.refetch();
        }
      })
      .catch(error => {
        removeLoading();
        snackbarServerError(error, {
          level: "error"
        });
      });
  }

  function showAmount(amount, allowZero) {
    return masterCurrency(amount, { doNotShowZero: !allowZero });
  }

  function returnCents() {
    const cents = [];
    balances.value.forEach(b =>
      b.balances ? b.balances.forEach(c => cents.push(c)) : null
    );
    return compact(cents);
  }

  function allPayables(calcWhat) {
    const cents = returnCents();
    let val = 0;
    switch (calcWhat) {
      case "incomeNotPaid":
        cents.forEach(c => {
          val += c.incomeNotPaidCents;
        });
        break;
      case "incomePaid":
        cents.forEach(c => {
          val += c.incomePaidCents;
        });
        break;
      case "incomeReturned":
        cents.forEach(c => {
          val -= c.incomeReturnedCents;
        });
        break;
      case "incomeTotal":
        cents.forEach(c => {
          val +=
            c.incomeNotPaidCents + c.incomePaidCents - c.incomeReturnedCents;
        });
        break;
      case "expenseNotPaid":
        cents.forEach(c => {
          val += c.expenseNotPaidCents;
        });
        break;
      case "expensePaid":
        cents.forEach(c => {
          val += c.expensePaidCents;
        });
        break;
      case "expenseTotal":
        cents.forEach(c => {
          val += c.expenseNotPaidCents + c.expensePaidCents;
        });
        break;
      case "payableBalance":
        cents.forEach(c => {
          val += c.incomeNotPaidCents - c.expenseNotPaidCents;
        });
        break;
      case "organiserEarnings":
        cents.forEach(c => {
          val += c.incomePaidCents - c.expensePaidCents - c.incomeReturnedCents;
        });
        break;
      case "organiserFinalEarnings":
        cents.forEach(c => {
          val +=
            c.incomeNotPaidCents +
            c.incomePaidCents -
            c.incomeReturnedCents -
            c.expenseNotPaidCents -
            c.expensePaidCents;
        });
        break;
    }

    return showAmount(val);
  }

  function totals(calcWhat) {
    const cents = returnCents();
    let val = 0;
    switch (calcWhat) {
      case "incomeNotPaid":
        {
          val += 0 - (returns.value.incomeNotPaidCents || 0);
          cents.forEach(c => {
            val += c.incomeNotPaidCents;
          });
        }
        break;
      case "incomePaid":
        {
          val += 0 - (returns.value.incomePaidCents || 0);
          cents.forEach(c => {
            val += c.incomePaidCents;
          });
        }
        break;
      case "incomeTotal":
        {
          val += 0 - (returns.value.incomeNotPaidCents || 0);
          val += 0 - (returns.value.incomePaidCents || 0);
          cents.forEach(c => {
            val += c.incomeNotPaidCents + c.incomePaidCents;
          });
        }
        break;
      case "expenseNotPaid":
        cents.forEach(c => {
          val += c.expenseNotPaidCents;
        });
        break;
      case "expensePaid":
        cents.forEach(c => {
          val += c.expensePaidCents;
        });
        break;
      case "expenseTotal":
        cents.forEach(c => {
          val += c.expenseNotPaidCents + c.expensePaidCents;
        });
        break;
      case "payableBalance":
        val += 0 - (returns.value.incomeNotPaidCents || 0);
        cents.forEach(c => {
          val += c.incomeNotPaidCents - c.expenseNotPaidCents;
        });
        break;
      case "organiserEarnings":
        val += returns.value.incomeNotPaidCents || 0;
        cents.forEach(c => {
          val += c.incomePaidCents - c.expensePaidCents - c.incomeReturnedCents;
        });
        break;
      case "organiserFinalEarnings":
        cents.forEach(c => {
          val +=
            c.incomeNotPaidCents +
            c.incomePaidCents -
            c.incomeReturnedCents -
            c.expenseNotPaidCents -
            c.expensePaidCents;
        });
        break;
    }
    return showAmount(val, true);
  }

  const {
    onResult: onEventResult,
    loading: eventLoading,
    refetch: eventRefetch
  } = useQuery(
    gql`
      query event($id: ID!) {
        event(id: $id) {
          id
          noncompetition
          useOnlinePaymentsForPrizeMoney
        }
      }
    `,
    () => ({ id: props.eventId }),
    { fetchPolicy: "cache-first" }
  );

  onEventResult(({ loading, data }) => {
    if (loading) return;
    event.value = data.event;
  });

  const {
    onResult: onCashierResult,
    loading: cashierLoading,
    refetch: cashierRefetch
  } = useQuery(
    gql`
    query eventCashierPersonBalances($eventId: ID!, $personId: ID!) {
      eventCashierPersonBalances(eventId: $eventId, personId: $personId) {
        id
        itemType
        timestamp
        entry {
          id
          ${AppShowEntryResolution.fragments.entryResolutionStatus}
          registration {
            id
            athletes {
              ${AppShowPeopleList.fragments.person}
            }
            horses {
              ${AppShowHorsesList.fragments.horse}
            }
            allowedMutations
          }
          allowedMutations
          competition {
            id
            code
            name
          }
        }
        registrationEventProduct {
          id
          allowedMutations
          entry {
            id
            competition {
              id
              ${AppShowCompetitionCodeAndName.fragments.competitionFields}
            }
          }
          registrationId
          registration {
            id
            athletes {
              ${AppShowPeopleList.fragments.person}
            }
            horses {
              ${AppShowHorsesList.fragments.horse}
              ${AppHorseSlot.fragments.horseFields}
            }
            allowedMutations
          }
          eventProduct {
            id
            name
          }
        }
        incomeNotPaidCents
        incomePaidCents
        incomeReturnedCents
        expenseNotPaidCents
        expensePaidCents
      }
    }
  `,
    () => ({
      eventId: props.eventId,
      personId: props.person.id
    })
  );

  onCashierResult(({ loading, data }) => {
    if (!loading && data.eventCashierPersonBalances?.length) {
      const newBalanceArray = [];

      data.eventCashierPersonBalances.forEach((balance, balanceIndex) => {
        // Ignore obejcts with itemType Returns, has no entry
        if (balance.itemType == "Returns") {
          returns.value = {
            id: balance.id,
            index: balanceIndex,
            itemType: balance.itemType,
            incomeNotPaidCents: balance.incomeNotPaidCents,
            incomePaidCents: balance.incomePaidCents,
            expenseNotPaidCents: balance.expenseNotPaidCents,
            expensePaidCents: balance.expensePaidCents
          };
          return;
        }
        // Fetch registrationId from entry or registrationEventProduct
        const registrationId =
          balance.entry?.registration.id ??
          balance.registrationEventProduct?.registrationId;
        let balances = [];

        data.eventCashierPersonBalances.forEach(data => {
          // Ignore obejcts with itemType Returns, has no entry
          if (data.itemType == "Returns") return;

          if (data.itemType == "Entry") {
            const selectedRegistrationId = data.entry.registration.id;
            if (registrationId == selectedRegistrationId) {
              balances.push({
                itemType: "Entry",
                id: data.id,
                entry: data.entry,
                code: data.entry.competition.code,
                name: data.entry.competition.name,
                incomeNotPaidCents: data.incomeNotPaidCents,
                incomePaidCents: data.incomePaidCents,
                incomeReturnedCents: data.incomeReturnedCents,
                expenseNotPaidCents: data.expenseNotPaidCents,
                expensePaidCents: data.expensePaidCents,
                timestamp: data.timestamp
              });
            }
          } else if (data.itemType == "RegistrationEventProduct") {
            const selectedRegistrationId =
              data.registrationEventProduct.registrationId;
            if (registrationId == selectedRegistrationId) {
              balances.push({
                itemType: "RegistrationEventProduct",
                id: data.id,
                registrationEventProductId: data.registrationEventProduct.id,
                competition: data.registrationEventProduct.entry?.competition,
                name: data.registrationEventProduct.eventProduct.name,
                registrationEventProductAllowedMutations:
                  data.registrationEventProduct.allowedMutations,
                code: null,
                incomeNotPaidCents: data.incomeNotPaidCents,
                incomePaidCents: data.incomePaidCents,
                incomeReturnedCents: data.incomeReturnedCents,
                expenseNotPaidCents:
                  data.expenseNotPaidCents == null
                    ? 0
                    : data.expenseNotPaidCents,
                expensePaidCents:
                  data.expensePaidCents == null ? 0 : data.expensePaidCents,

                timestamp: data.timestamp
              });
            }
          }
        });
        newBalanceArray.push({
          id: balance.id,
          index: balanceIndex,
          itemType: balance.itemType,
          registrationId: registrationId,
          registration:
            balance.entry?.registration ??
            balance.registrationEventProduct?.registration,
          balances:
            balances.length > 0 ? orderBy(balances, "code", "asc") : null
        });
      });

      balances.value = uniqBy(newBalanceArray, "registrationId");
    }
  });

  return {
    masterCurrency,

    balances,
    returns,

    unregister,
    allPayables,
    totals,
    showAmount,

    cashierLoading,
    cashierRefetch,

    event,
    eventLoading,
    eventRefetch
  };
}
